/**
 * Sub-aba "Vínculo Produto Aba": quais abas da ficha cada produto contratado abre, e em que ordem.
 * Porte de `admin.html:1991-2043` (tela) e `8996-9193` (comportamento); linhas 129 a 135 do
 * inventário.
 *
 * O QUE A PESSOA FAZ AQUI, igual ao legado: escolhe produto e aba, vê (só leitura) a visibilidade
 * que a aba tem em "Visibilidade Aba", e adiciona o vínculo; vê os vínculos num cartão por produto;
 * remove um vínculo; e reordena as abas de um produto arrastando, com o mouse ou com o dedo, e a
 * ordem é gravada ao soltar.
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma.
import { Botao, Campo, Selecao } from '@complexity/ui';
// As chamadas da área.
import {
  inserirVinculoProdutoAba,
  listarVinculosProdutoAba,
  listarVisibilidadeAbas,
  removerVinculoProdutoAba,
  salvarOrdemDosVinculos,
} from '@studiolu/dados/areas/sistema';
// A linha de `parametros_produto_aba`.
import type { LinhaParametrosProdutoAba } from '@studiolu/dados';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área, e o status que some em 3 s (`mostrarStatusParam`, `admin.html:9171`).
import { useMemoria, useStatusDaArea } from './memoria.js';
// A linha compacta dos campos curtos.
import { LinhaCompacta } from '../financeiro/apoio.js';
// Os catálogos do legado.
import { PARAM_ABAS_LABEL, PARAM_PRODUTOS_LABEL, PARAM_VIS_LABEL, opcoesDe } from './catalogos.js';
// O arraste de mouse e de toque.
import { useArrasteDeLinhas } from './arraste.js';
// As peças visuais da área.
import {
  AlcaDeArraste,
  BotaoDaLinha,
  CabecalhoDeColunas,
  CabecalhoDeProduto,
  CartaoParam,
  LinhaParam,
  NotaSecao,
  TextoParam,
  type Carga,
  type PropsDaSubAba,
} from './pecas.js';

// As opções dos dois `<select>`, na ordem do HTML do legado (`admin.html:1998-2028`).
const OPCOES_DE_PRODUTO = opcoesDe(PARAM_PRODUTOS_LABEL);
const OPCOES_DE_ABA = opcoesDe(PARAM_ABAS_LABEL);

/**
 * O texto do campo "Visibilidade", só leitura (`atualizarOpcoesVisibilidadeVinculo`,
 * `admin.html:8999-9016`), e o valor que vai para o banco (o campo escondido do legado).
 */
function visibilidadeDaAba(aba: string, mapa: Readonly<Record<string, string>>): { texto: string; valor: string } {
  if (!aba) return { texto: '(selecione a aba)', valor: '' };
  /**
   * ABA SEM LINHA VALE "ambos", e o campo mostra isso.
   *
   * MUDANÇA PEDIDA PELO FUNDADOR em 19/09/2026, 21:00, depois de ver a tela: "nao haverá selecao
   * de visibilidade apenas vai mostrar o que tem em Visibilidade Aba". O legado mostrava aqui
   * "Nenhuma visibilidade configurada pra esta aba" e travava o Adicionar, o que contradiz a
   * própria tela "Visibilidade Aba": lá, aba sem linha aparece marcada como **Ambos**
   * (`admin.html:8959`, e a mesma regra em `visibilidade.tsx`). Ou seja, "nenhuma" nunca foi um
   * estado real do sistema: era o texto de uma tela que não sabia o padrão da outra.
   *
   * O valor gravado segue o mesmo padrão, e por isso o vínculo passa a ser adicionável sem
   * ninguém ter que visitar a outra sub-aba antes.
   */
  const visibilidade = mapa[aba] || 'ambos';
  return { texto: PARAM_VIS_LABEL[visibilidade] || visibilidade, valor: visibilidade };
}

/**
 * Os produtos na ordem do catálogo, e os desconhecidos no fim em ordem `pt-BR`
 * (`admin.html:9041-9050`).
 */
function produtosEmOrdem(linhas: readonly LinhaParametrosProdutoAba[]): string[] {
  const presentes = new Set(linhas.map((l) => l.produto));
  const conhecidos = Object.keys(PARAM_PRODUTOS_LABEL).filter((p) => presentes.has(p));
  const desconhecidos = [...presentes].filter((p) => !PARAM_PRODUTOS_LABEL[p]).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return [...conhecidos, ...desconhecidos];
}

export function SubAbaVinculos({
  confirmar,
  alertar,
  recarga,
  mapaDeVisibilidade,
}: PropsDaSubAba & {
  // O mapa lido em "Visibilidade Aba" (`visibilidadeAbasCarregadas`).
  readonly mapaDeVisibilidade: Readonly<Record<string, string>>;
}) {
  const { dados } = usePainel();
  const [carga, setCarga] = useState<Carga<null>>({ tipo: 'carregando' });
  // Os vínculos em memória (`parametrosCarregados`), que o legado altera sem reler o banco.
  const [vinculos, setVinculos] = useState<readonly LinhaParametrosProdutoAba[]>([]);
  // A mesma lista numa referência, para as checagens do clique lerem o valor mais recente.
  const vinculosAtuais = useRef<readonly LinhaParametrosProdutoAba[]>([]);
  // O formulário: produto e aba começam em "(selecione)". Na memória da área, porque no legado são
  // os `<select>` fixos `#param-novo-produto` e `#param-nova-aba` (`admin.html:1996-2030`).
  const [produto, setProduto] = useMemoria('sistema:vinculos:produto', '');
  const [aba, setAba] = useMemoria('sistema:vinculos:aba', '');
  // `#param-status`, fora da lista (`admin.html:2040`): não some quando a lista é relida, nem ao ir
  // a outra área e voltar.
  const [status, mostrarStatus] = useStatusDaArea('sistema:vinculos:status');

  // Troca a lista em memória e a referência juntas.
  const guardar = useCallback((lista: readonly LinhaParametrosProdutoAba[]) => {
    vinculosAtuais.current = lista;
    setVinculos(lista);
  }, []);

  /**
   * O MAPA DE VISIBILIDADE LIDO AQUI, e não só recebido pronto.
   *
   * O DEFEITO MEDIDO em 19/09/2026, apontado pelo fundador: entrando direto em "Vínculo Produto
   * Aba", o campo Visibilidade dizia "Nenhuma visibilidade configurada pra esta aba" com as
   * visibilidades cadastradas. A causa: o mapa nascia vazio e só era preenchido quando alguém abria
   * a sub-aba "Visibilidade Aba", que é quem fazia a leitura. Quem entra direto nesta nunca via
   * nada, e o campo mentia sobre o cadastro.
   *
   * Por isso esta sub-aba lê o mapa por conta própria. O que vier do pai continua valendo como
   * reserva, para a tela mostrar algo mesmo enquanto esta leitura ainda não voltou.
   */
  const [mapaLido, setMapaLido] = useState<Readonly<Record<string, string>>>({});

  // `carregarParametros` (`admin.html:9022-9032`).
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    const resultado = await listarVinculosProdutoAba(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    guardar(resultado.dados ?? []);
    setCarga({ tipo: 'pronta', dados: null });
    // A leitura do mapa vem DEPOIS de a lista aparecer, e a falha dela não derruba a tela: sem o
    // mapa, o campo volta a dizer que a aba não tem visibilidade, que é o texto do legado.
    const visibilidades = await listarVisibilidadeAbas(dados);
    if (!visibilidades.ok) return;
    const mapa: Record<string, string> = {};
    // Linha sem visibilidade vale "ambos", como o `r.visibilidade || 'ambos'` do legado.
    for (const linha of visibilidades.dados ?? []) mapa[linha.aba] = linha.visibilidade || 'ambos';
    setMapaLido(mapa);
  }, [dados, guardar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  /**
   * A visibilidade da aba escolhida, dos DOIS mapas somados, e nesta ordem.
   *
   * O lido aqui entra por baixo, e o que vem do pai vence: o do pai é atualizado no instante em que
   * alguém salva em "Visibilidade Aba" (`aoTrocarVisibilidade`), então ele é o mais novo. Preferir o
   * meu desfaria a troca recém-salva até a próxima leitura, que é um defeito mais sutil e pior que o
   * original, porque aparece só para quem acabou de salvar.
   */
  const visibilidade = visibilidadeDaAba(aba, { ...mapaLido, ...mapaDeVisibilidade });

  // "Adicionar" (`admin.html:9177-9193`).
  const adicionar = useCallback(async () => {
    // Só produto e aba: a visibilidade sempre tem valor quando há aba, pelo padrão "ambos".
    if (!produto || !aba) {
      // A MENSAGEM DEIXOU DE PEDIR VISIBILIDADE em 19/09/2026, por pedido do fundador: "msg
      // errada... nao haverá selecao de visibilidade". Ela é só leitura desde sempre, e agora tem
      // padrão ("Ambos"), então pedir que a pessoa a selecione mandava procurar um campo que não
      // existe. O que ainda falta escolher é produto e aba, e é só isso que a frase diz.
      mostrarStatus('Selecione produto e aba.');
      return;
    }
    if (vinculosAtuais.current.some((r) => r.produto === produto && r.aba === aba)) {
      mostrarStatus('Este vínculo já existe.');
      return;
    }
    const resultado = await inserirVinculoProdutoAba(dados, produto, aba, visibilidade.valor);
    // O erro de gravar vai em aviso, e não no status (`admin.html:9185`).
    if (!resultado.ok) {
      await alertar('Erro ao adicionar: ' + resultado.erro.mensagem);
      return;
    }
    // Entra na lista e a lista volta à ordem produto e aba, como o `sort` do legado.
    const nova = [...vinculosAtuais.current, resultado.dados].sort(
      (a, b) => a.produto.localeCompare(b.produto) || a.aba.localeCompare(b.aba),
    );
    guardar(nova);
    // Produto e aba voltam a "(selecione)", e com eles o texto da visibilidade.
    setProduto('');
    setAba('');
    mostrarStatus('Vínculo adicionado.');
  }, [produto, aba, visibilidade.valor, dados, alertar, guardar, mostrarStatus, setProduto, setAba]);

  // "Remover" (`admin.html:9088-9098`). O id é número: a coluna é `bigint`, e o `parseInt` do legado vale.
  const remover = useCallback(
    async (id: number) => {
      if (!(await confirmar('Remover este vínculo?'))) return;
      const resultado = await removerVinculoProdutoAba(dados, id);
      if (!resultado.ok) {
        await alertar('Erro ao remover: ' + resultado.erro.mensagem);
        return;
      }
      guardar(vinculosAtuais.current.filter((r) => r.id !== id));
      mostrarStatus('Vínculo removido.');
    },
    [dados, confirmar, alertar, guardar, mostrarStatus],
  );

  // A ordem de um produto, gravada ao soltar (`persistirOrdemAbas`, `admin.html:9156-9169`).
  const gravarOrdem = useCallback(
    async (idsEmOrdem: readonly number[]) => {
      mostrarStatus('Salvando ordem...');
      const resultado = await salvarOrdemDosVinculos(dados, idsEmOrdem);
      if (!resultado.ok) {
        mostrarStatus('Erro ao salvar ordem: ' + resultado.erro.mensagem);
        return;
      }
      // A ordem nova em memória; a tela já está na ordem certa, e continua.
      guardar(
        vinculosAtuais.current.map((r) => {
          const indice = idsEmOrdem.indexOf(r.id);
          return indice === -1 ? r : { ...r, ordem: indice };
        }),
      );
      mostrarStatus('Ordem salva.');
    },
    [dados, guardar, mostrarStatus],
  );

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Define quais abas do portal ficam disponíveis para cada produto contratado, e quem pode visualizá-las.
      </NotaSecao>

      <LinhaCompacta>
        <Selecao
          rotulo="Produto"
          vazio="(selecione)"
          opcoes={OPCOES_DE_PRODUTO}
          value={produto}
          onChange={(evento) => setProduto(evento.target.value)}
        />
        <Selecao
          rotulo="Aba"
          vazio="(selecione)"
          opcoes={OPCOES_DE_ABA}
          value={aba}
          onChange={(evento) => setAba(evento.target.value)}
        />
        {/* Só leitura: a visibilidade vem de "Visibilidade Aba" (`admin.html:2032-2034`). */}
        <Campo rotulo="Visibilidade" type="text" readOnly value={visibilidade.texto} />
        <Botao onClick={() => void adicionar()}>Adicionar</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-vinculos" />

      {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
      {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
      {carga.tipo === 'pronta' && vinculos.length === 0 && <TextoParam>Nenhum vínculo cadastrado.</TextoParam>}
      {carga.tipo === 'pronta' && vinculos.length > 0 && (
        <div className="flex flex-col gap-cx-4" data-testid="lista-vinculos">
          {produtosEmOrdem(vinculos).map((p) => (
            <CartaoDoProduto
              key={p}
              produto={p}
              linhas={vinculos.filter((r) => r.produto === p)}
              aoRemover={(id) => void remover(id)}
              aoReordenar={(ids) => void gravarOrdem(ids)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** O cartão de um produto, com as abas na ordem manual e o arraste só dentro dele. */
function CartaoDoProduto({
  produto,
  linhas,
  aoRemover,
  aoReordenar,
}: {
  readonly produto: string;
  readonly linhas: readonly LinhaParametrosProdutoAba[];
  readonly aoRemover: (id: number) => void;
  readonly aoReordenar: (ids: readonly number[]) => void;
}) {
  // As abas na ordem manual; sem ordem vão para o fim, como o `?? 999` do legado (`admin.html:9054`).
  const ordenadas = [...linhas].sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));
  const porChave = new Map(ordenadas.map((r) => [String(r.id), r]));
  const arraste = useArrasteDeLinhas(
    ordenadas.map((r) => String(r.id)),
    // Ao soltar, os ids na ordem da tela, como o `querySelectorAll` do legado.
    (ordem) => aoReordenar(ordem.map((chave) => Number(chave))),
  );
  const qtd = ordenadas.length;
  return (
    <div ref={arraste.refDoGrupo} data-produto={produto}>
      <CartaoParam>
        <CabecalhoDeProduto titulo={PARAM_PRODUTOS_LABEL[produto] || produto} selo={`${qtd} ${qtd === 1 ? 'aba' : 'abas'}`} />
        <CabecalhoDeColunas recuoDeAlca>
          <span className="min-w-0 flex-1">Aba</span>
          <span className="w-[160px] shrink-0">Visibilidade</span>
          <span className="w-[76px] shrink-0" />
        </CabecalhoDeColunas>
        {arraste.ordem.map((chave) => {
          const linha = porChave.get(chave);
          if (!linha) return null;
          return (
            <LinhaParam
              key={chave}
              data-id={chave}
              {...arraste.propsDaLinha(chave)}
              // Cada linha marcada fica apagada (`.arrastando`, opacidade 0,4), uma a uma como no legado.
              className={arraste.marcadas.has(chave) ? 'opacity-40' : ''}
            >
              <AlcaDeArraste />
              <span className="min-w-0 flex-1 text-cx-sm [overflow-wrap:anywhere]">
                {PARAM_ABAS_LABEL[linha.aba] || linha.aba}
              </span>
              <span className="min-w-0 basis-full text-cx-xs text-texto-suave md:w-[160px] md:shrink-0 md:basis-auto">
                {/* O rótulo, ou o valor cru; `null` sai como o texto "null", igual ao template do legado (`admin.html:9060`). */}
                {PARAM_VIS_LABEL[linha.visibilidade ?? ''] || String(linha.visibilidade)}
              </span>
              <span className="ml-auto shrink-0 text-right md:w-[76px]">
                <BotaoDaLinha perigo onClick={() => aoRemover(linha.id)}>Remover</BotaoDaLinha>
              </span>
            </LinhaParam>
          );
        })}
      </CartaoParam>
    </div>
  );
}
